import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { useLocation } from "react-router";

/**
 * A dynamic import that failed - usually a stale chunk after a deploy, or a
 * dropped connection - rather than a bug in the page itself.
 */
function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  return /dynamically imported module|Loading chunk|ChunkLoadError|Importing a module script failed/i.test(
    `${error.name} ${error.message}`,
  );
}

interface RouteErrorBoundaryProps {
  children: ReactNode;
  /**
   * Changing this clears a caught error. Pass the current location so a caught
   * render error does not outlive the URL that caused it - otherwise a bad
   * `?range=` value would leave the boundary stuck even after the user picks a
   * valid one.
   */
  resetKey?: string;
}

interface RouteErrorBoundaryState {
  error: Error | null;
  resetKey: string | undefined;
}

export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null, resetKey: undefined };

  static getDerivedStateFromError(
    error: Error,
  ): Partial<RouteErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: RouteErrorBoundaryProps,
    state: RouteErrorBoundaryState,
  ): Partial<RouteErrorBoundaryState> | null {
    if (props.resetKey !== state.resetKey) {
      return { error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Route render failed:", error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (isChunkLoadError(error)) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-muted-foreground">
            Failed to load page. Check your connection and try again.
          </p>
          <button
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary-hover"
            onClick={this.handleReload}
          >
            Reload
          </button>
        </div>
      );
    }

    return (
      <div
        className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-6 text-center"
        data-testid="route-error"
      >
        <h2 className="text-lg font-medium">
          This page could not be displayed
        </h2>
        <p className="max-w-md text-sm text-muted-foreground">
          Something in this view failed to render. If you followed a link with
          filters in it, one of them may not be valid - try changing a filter,
          or reload the page.
        </p>
        <button
          className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary-hover"
          onClick={this.handleReload}
        >
          Reload
        </button>
      </div>
    );
  }
}

/** Route error boundary that resets whenever the location changes. */
export function LocationScopedErrorBoundary({
  children,
}: {
  children: ReactNode;
}) {
  const location = useLocation();
  return (
    <RouteErrorBoundary resetKey={`${location.pathname}${location.search}`}>
      {children}
    </RouteErrorBoundary>
  );
}
